import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Bell, ShieldAlert, LifeBuoy, MessageSquare, Loader2 } from "lucide-react";
import { formatWhen } from "@/lib/complaints";

const ICONS = {
  complaint_new: ShieldAlert,
  complaint_message: MessageSquare,
  complaint_status: LifeBuoy,
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Poll unread count every 30s
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (!cancelled) setCount(data.unread_count || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // When opening, fetch latest
  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    api.get("/notifications?limit=15")
      .then((r) => {
        setItems(r.data.notifications || []);
        setCount(r.data.unread_count || 0);
      })
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!user) return null;

  const onClick = async (n) => {
    if (!n.is_read) {
      api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
      setItems((arr) => arr.map((x) => x.notification_id === n.notification_id ? { ...x, is_read: true } : x));
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((arr) => arr.map((x) => ({ ...x, is_read: true })));
    setCount(0);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-full p-2 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/50">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span data-testid="notification-count" className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C05A3C] text-white text-[10px] font-semibold flex items-center justify-center">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 max-h-[70vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
          <span className="font-semibold text-sm">Notifications</span>
          {count > 0 && (
            <button onClick={markAll} className="text-xs text-[#C05A3C] hover:underline" data-testid="mark-all-read">Mark all read</button>
          )}
        </div>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">You&apos;re all caught up.</div>
        ) : (
          <ul>
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <li key={n.notification_id}>
                  <button
                    type="button"
                    onClick={() => onClick(n)}
                    data-testid={`notif-${n.notification_id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-[#F3EFE9] flex items-start gap-3 border-b border-black/5 last:border-0 ${n.is_read ? "" : "bg-[#FBF6EE]"}`}
                  >
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${n.is_read ? "bg-black/5" : "bg-[#C05A3C]/10"}`}>
                      <Icon className={`w-4 h-4 ${n.is_read ? "text-muted-foreground" : "text-[#C05A3C]"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{formatWhen(n.created_at)}</div>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#C05A3C] mt-1.5"></span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
